import { useMemo, useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';

import { ReactComponent as IconFindLocation } from '../AdForm/PropertyLocation/findLocation.svg';
import { LocationAutocomplete } from '../LocationAutocomplete/LocationAutocomplete';
import { useLocationAutocomplete } from '../LocationAutocomplete/useLocationAutocomplete';
import { MapUi } from '../Map/Map';
import { FormInput, LabelComponent } from '../uiComponents';
import type { LocationModel } from '../../types/location';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';

import { EditorSection } from './EditorSection';
import { AiReviewFieldNotice, AiReviewLocationNotice, useAiListingReview } from './AiListingReview';
import {
  EMPTY_PROPERTY_AD_LOCATION,
  normalizeExistingPropertyAdLocation,
  normalizeNewPropertyAdBuildingName,
  normalizeNewPropertyAdLocation,
  PROPERTY_AD_LOCATION_FIELD_NAMES,
  type PropertyAdLocationValues,
} from './propertyAdLocation';

interface PropertyLocationSectionProps {
  initialAddress: string;
  initialIsNewLocation: boolean;
}

export const PropertyLocationSection = ({
  initialAddress,
  initialIsNewLocation,
}: PropertyLocationSectionProps) => {
  const aiReview = useAiListingReview();
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<PropertyAdEditorFormValues>();
  const propertyKind = useWatch({ control, name: 'propertyKind' });
  const addressEn = useWatch({ control, name: 'addressEn' });
  const coordinatesLat = useWatch({ control, name: 'coordinatesLat' });
  const coordinatesLong = useWatch({ control, name: 'coordinatesLong' });
  const locationId = useWatch({ control, name: 'locationId' });
  const districtId = useWatch({ control, name: 'districtId' });
  const buildingName = useWatch({ control, name: 'buildingName' });
  const [isNewLocation, setIsNewLocation] = useState(initialIsNewLocation);
  const {
    inputAddressValue,
    isLoadingLocations,
    locations,
    selectedLocation,
    setInputAddressValue,
    setSelectedLocation,
  } = useLocationAutocomplete({
    defaultValue: initialAddress,
    searchByDistrict: isNewLocation,
    searchWithProperty: true,
    skipInitialSearch: true,
  });

  const currentLocationValues = (): PropertyAdLocationValues => {
    const values = getValues();

    return {
      addressEn: values.addressEn,
      nameEn: values.nameEn,
      title: values.title,
      placeId: values.placeId,
      coordinatesLat: values.coordinatesLat,
      coordinatesLong: values.coordinatesLong,
      locationId: values.locationId,
      districtId: values.districtId,
      buildingName: values.buildingName,
    };
  };

  const applyLocationValues = (values: PropertyAdLocationValues) => {
    const currentValues = currentLocationValues();

    PROPERTY_AD_LOCATION_FIELD_NAMES.forEach((fieldName) => {
      if (currentValues[fieldName] !== values[fieldName]) {
        setValue(fieldName, values[fieldName], { shouldDirty: true });
      }
    });
  };

  const handleLocationSelect = (location: LocationModel) => {
    setSelectedLocation(location);
    applyLocationValues(
      isNewLocation
        ? normalizeNewPropertyAdLocation(location, buildingName)
        : normalizeExistingPropertyAdLocation(location)
    );
    aiReview?.resolveLocation();
  };

  const clearLocation = () => {
    setSelectedLocation(null);
    applyLocationValues(EMPTY_PROPERTY_AD_LOCATION);
  };

  const enterNewLocation = () => {
    setIsNewLocation(true);
    setInputAddressValue('');
    clearLocation();
  };

  const returnToExistingLocation = () => {
    setIsNewLocation(false);
    setInputAddressValue('');
    clearLocation();
  };

  const mapLocation = useMemo<Partial<LocationModel> | null>(() => {
    if (selectedLocation) return selectedLocation;

    if (!coordinatesLat || !coordinatesLong) return null;
    const latitude = Number(coordinatesLat);
    const longitude = Number(coordinatesLong);
    if (!addressEn || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

    return {
      id: locationId ?? districtId ?? 'hydrated-location',
      latitude,
      longitude,
    };
  }, [addressEn, coordinatesLat, coordinatesLong, districtId, locationId, selectedLocation]);

  const locationLabel = isNewLocation ? 'Enter District name' : 'Enter location';

  return (
    <EditorSection
      cardTestId='property-location-card'
      description='Search for an existing location or add an unconfirmed property location'
      title='Property Location'
      titleId='property-location-title'
    >
      <AiReviewLocationNotice />
      <AiReviewFieldNotice target='location' />
      <Stack
        alignItems={{ xs: 'stretch', sm: 'flex-end' }}
        direction={{ xs: 'column', sm: 'row' }}
        flexWrap={{ sm: 'wrap' }}
        gap={2.5}
        minWidth={0}
      >
        <Box sx={{ flex: { sm: '1 1 320px' }, minWidth: { xs: 0, sm: 220 } }}>
          <LabelComponent isRequired label={locationLabel} />
          <LocationAutocomplete
            ariaLabel={locationLabel}
            clearOnDependenciesChange={false}
            handleClear={clearLocation}
            inputAddressValue={inputAddressValue}
            isAddNewLocationVisible={isNewLocation}
            isLoading={isLoadingLocations}
            locations={locations}
            onSelect={handleLocationSelect}
            propertyKind={propertyKind ?? undefined}
            selectedLocation={selectedLocation}
            setInputAddressValue={setInputAddressValue}
          />
        </Box>

        {isNewLocation && (
          <Box sx={{ flex: { sm: '1 1 260px' }, minWidth: { xs: 0, sm: 220 } }}>
            <FormInput
              fullWidth
              label='Enter building name'
              name='buildingName'
              onChange={(event) =>
                applyLocationValues(
                  normalizeNewPropertyAdBuildingName(currentLocationValues(), event.target.value)
                )
              }
              placeholder='Enter building name'
              required
              uiVariant='blank'
            />
          </Box>
        )}

        {isNewLocation ? (
          <Button
            onClick={returnToExistingLocation}
            sx={{
              borderRadius: '14px',
              flex: '0 0 auto',
              fontWeight: 600,
              height: 48,
              minWidth: 120,
              paddingX: 2.5,
              textTransform: 'none',
              width: { xs: '100%', sm: 'auto' },
            }}
            type='button'
            variant='outlined'
          >
            Cancel
          </Button>
        ) : (
          <Button
            onClick={enterNewLocation}
            startIcon={<IconFindLocation aria-hidden='true' />}
            sx={{
              flex: '0 0 auto',
              fontSize: 16,
              fontWeight: 800,
              justifyContent: 'flex-start',
              lineHeight: '24px',
              minWidth: 0,
              padding: 0,
              textAlign: 'left',
              textTransform: 'none',
            }}
            type='button'
            variant='text'
          >
            Didn&apos;t find the location?
          </Button>
        )}
      </Stack>

      {errors.locationId?.message && (
        <Typography color='error' component='p' marginTop={1.5} role='alert' variant='body2'>
          {errors.locationId.message}
        </Typography>
      )}

      <Box
        data-testid='property-location-map'
        sx={{ borderRadius: '14px', marginTop: 3, minWidth: 0, overflow: 'hidden', width: '100%' }}
      >
        <MapUi selectedLocation={mapLocation} isAddNewLocationVisible={isNewLocation} />
      </Box>
    </EditorSection>
  );
};
